import { Button, Card, Col, message, Row, Spin } from "antd";
import React, { useEffect, useState } from "react";
import moment from "moment/moment";

import InsurancePatientsTable from "./InsurancePatientsTable";
import InsuranceUploadedFile from "./InsuranceUploadedFile";

import { controller as treatmentsController } from "../TreatmentPlans/controller";
import { statusLableColor } from "./statusLableColor";
import { controller } from "../../controller";

import calendarCircle from "../../assets/icons/calendar-circle.svg";
import directBox from "../../assets/icons/directbox-receive.svg";
import arrowLeft from "../../assets/icons/arrow-left.svg";
import users from "../../assets/icons/profile-2user.svg";

export default function InsuranceDetailsPatientBase({ predeterminationOrPostPayment, setSelectedInsurance, selectedInsurance }) {
  const [isLoadingInsuranceData, setIsLoadingInsuranceData] = useState(true);
  const [isApproved, setIsApproved] = useState(false);
  const [data, setData] = useState(null);
  const [isApproving, setIsApproving] = useState(false);
  const [isApprovingInsurance, setIsApprovingInsurance] = useState([]); // Loading for the time that all patients are fetching
  const [isGettingPatients, setIsGettingPatients] = useState(false); // Loading for the time that all patients are fetching
  const [isGettingProcedures, setIsGettingProcedures] = useState(false); // Loading for the time that all patients are fetching
  const [allPatients, setAllPatients] = useState([]); // All office patients
  const [allProcedures, setAllProcedures] = useState([]);
  // const [emptyPatientDropdowns, setEmptyPatientDropdowns] = useState([]); // To see what dropdown are empty to active or disable the approve button
  const [selectedPaymentType, setSelectedPaymentType] = useState(undefined);
  const [allTempPatients, setAllTempPatients] = useState([]);
  const [insuranceStatus, setInsuranceStatus] = useState(undefined);
  const [insuranceStatusLabelColor, setInsuranceStatusLabelColor] = useState({ statusBackgroundColor: null, statusColor: null });
  const [approvedPatients, setApprovedPatients] = useState([]);
  const [allPaymentTypes, setAllpaymentTypes] = useState([]);
  const [markedProcedures, setMarkedProcedures] = useState([]);
  const [approvedPredeterminations, setApprovedPredeterminations] = useState([]);
  const [allInsurancePatients, setAllInsurancePatients] = useState([]);

  // SECTION: useEffects
  // Sending a request to server every 5 second for getting the status to update the UI until the status changes to `Pending`
  useEffect(function () {
    async function getInsuranceStatus() {
      const response = await controller.fetchPredeterminationDetails(selectedInsurance.id);

      if (response.json.status === "processing") return;

      setData(response.json);
      setInsuranceStatus(response.json.status === "partially_accepted" ? "pending" : response.json.status);
      clearInterval(intervalID);
    }
    getInsuranceStatus();

    const intervalID = setInterval(getInsuranceStatus, 5000);

    return () => clearInterval(intervalID);
  }, []);

  // Getting all office patients
  useEffect(function () {
    getAllPatients();
  }, []);

  // Getting all procedures
  useEffect(function () {
    async function getAllProcedures() {
      try {
        setIsGettingProcedures(true);
        const res = await treatmentsController.getProcedure(0, "");
        setAllProcedures(res.json);
      } catch (err) {
        message.error("Something went wrong while fetching procedures");
        console.log(err);
      } finally {
        setIsGettingProcedures(false);
      }
    }
    getAllProcedures();
  }, []);

  // Getting uploaded files data
  useEffect(
    function () {
      if (selectedInsurance === null) return;
      getInsuranceDetailsData();
    },
    [selectedInsurance]
  );

  // Getting insurance details
  const fetchData = async () => {
    if (selectedInsurance.id) {
      const response = await controller.fetchPredeterminationDetails(selectedInsurance.id);

      setData(response.json);
      setInsuranceStatus(response.json.status === "partially_accepted" ? "pending" : response.json.status);
      setAllTempPatients(response.json.temp_patients);
    }
  };

  // Handle the status label background color and color
  useEffect(
    function () {
      const { statusBackgroundColor, statusColor } = statusLableColor(insuranceStatus);
      setInsuranceStatusLabelColor({ statusBackgroundColor, statusColor });
    },
    [insuranceStatus]
  );

  // Store all payment types after fetching data
  useEffect(
    function () {
      if (data === null) return;

      setAllpaymentTypes(data.payment_types);
      setAllInsurancePatients(data.patients.map((patient) => ({ ...patient })));
    },
    [data]
  );

  // SECTION: Functions

  async function getAllPatients() {
    try {
      setIsGettingPatients(true);
      const res = await controller.getPatient2();
      setAllPatients(res);
    } catch (err) {
      message.error("Something went wrong while fetching patients");
      console.log(err);
    } finally {
      setIsGettingPatients(false);
    }
  }

  // Fetch the insurance data
  async function getInsuranceDetailsData() {
    try {
      setIsLoadingInsuranceData(true);
      await fetchData();
    } catch (err) {
      message.error("Something went wrong while getting insurance details data.");
      console.log(err);
    } finally {
      setIsLoadingInsuranceData(false);
    }
  }

  if (isLoadingInsuranceData || isGettingPatients || isGettingProcedures)
    return (
      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, gap: 10 }}>
        <Spin />
        loading...
      </div>
    );

  // const allInsurancePatients = data.patients.map((patient) => ({ ...patient }));

  // This approves the insurance in the predetermination page
  const handleApproveInsurance = async (patientID, insurancePatient) => {
    try {
      setIsApproving(true);

      const response = await controller.setTreatmentPatientId({
        patient_id: patientID,
        temporary_patient_id: insurancePatient.id,
        treatment_procedure_ids: markedProcedures,
        predetermination_request_id: selectedInsurance.id,
        predetermination_case_id: insurancePatient.predetermination_request_submission_id,
      });

      if (response.status === 200) {
        message.success("Approved successfully!");

        if (response.json.predetermination_file_status !== "partially_accepted") setIsApproved(true);
        setInsuranceStatus(response.json.predetermination_file_status === "partially_accepted" ? "partially approved" : "approved");
        setApprovedPredeterminations((prevApprovedPatients) => [...prevApprovedPatients, insurancePatient.id]);
      }
    } catch (e) {
      console.error(e);
      message.error("Internal server Error");
    } finally {
      setIsApproving(false);
      await fetchData();
    }
  };

  // Approve post payment insurances
  async function handleApprovePostPayment(patientID) {
    if (!selectedPaymentType) {
      message.error("Please select your payment type");
      return;
    }

    try {
      setIsApprovingInsurance((prevIDs) => [...prevIDs, patientID]);

      // Getting all patient procedure ids
      const procedureIDs = allInsurancePatients.flatMap((patient) =>
        patient.id === patientID ? patient.insurances.map((insurance) => insurance.id) : []
      );

      const dataToSend = { procedure_ids: procedureIDs, patient_id: patientID, payment_type: selectedPaymentType };

      const response = await controller.approvePostPayment(dataToSend);

      if (response.status < 250) {
        message.success("Approved successfully!");
        // Add Approved patient to thist list to disable it's procedures and the petient's dropdown
        setApprovedPatients((prevApprovedPatients) => [...prevApprovedPatients, patientID]);
        // setInsuranceStatus(response.json.status);

        // Approve the whole insurance whel all patients are approved
        if (approvedPatients.length + 1 === allInsurancePatients.length) {
          setIsApproved(true);
          setInsuranceStatus("approved");
        }
      } else {
        message.error(response.json ? JSON.stringify(response.json) : "Something went wrong!");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong while approving patient");
    } finally {
      setIsApprovingInsurance((prevIDs) => prevIDs.filter((id) => id !== patientID));
    }
  }

  return (
    <div style={{ padding: "20px 40px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 25 }}>
        <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 0 }}>{selectedInsurance && selectedInsurance.name}'s Details</p>

        <Button
          type="primary"
          style={{
            border: "2px solid #6b43b5",
            color: "#6b43b5",
            backgroundColor: "#ffffff",
            display: "flex",
            alignItems: "center",
            fontSize: 16,
          }}
          onClick={() => setSelectedInsurance(null)}
        >
          <img src={arrowLeft} alt="arrow-left" />
          Back
        </Button>
      </div>

      <Card>
        <div>
          <Row className="post-payment-status">
            <p style={{ fontSize: 18, marginBottom: 0 }}>Patient Insurace Information</p>

            {/* Status */}
            <div>
              <span style={{ color: "#848696", fontSize: 16 }}>Status: </span>
              <span
                style={{
                  backgroundColor: insuranceStatusLabelColor.statusBackgroundColor,
                  color: insuranceStatusLabelColor.statusColor,
                  padding: "1px 24px",
                  borderRadius: 100,
                  fontSize: 14,
                  textTransform: "capitalize",
                }}
              >
                {insuranceStatus === "accepted" || insuranceStatus === "approved" || isApproved
                  ? "approved"
                  : insuranceStatus || (selectedInsurance && selectedInsurance.status)}
              </span>
            </div>
          </Row>

          <Row className="submission-received-dates-files">
            {/* Submission Date */}
            <Col className="submission-received-dates-files-item">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <img src={calendarCircle} alt="calendar-circle" />
                <span style={{ color: "#848696" }}>Submission Date:</span>
              </div>

              <span>{data && data.approval_date && moment(data.approval_date).format("YYYY-MM-DD")}</span>
            </Col>

            {/* Received Date */}
            <Col className="submission-received-dates-files-item">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <img src={directBox} alt="direct-box" />
                <span style={{ color: "#848696" }}>Received Date:</span>
              </div>

              <span>{data && data.upload_date && moment(data.upload_date).format("YYYY-MM-DD")}</span>
            </Col>

            {/* Number of Patients */}
            <Col className="submission-received-dates-files-item">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <img src={users} alt="folder" />
                <span style={{ color: "#848696" }}>Patients: </span>
              </div>

              <span>{selectedInsurance && selectedInsurance.patients_count}</span>
            </Col>
          </Row>
        </div>

        <Card>
          <p style={{ fontSize: 18 }}>Patient Insurance Result</p>

          <div>
            <InsurancePatientsTable
              loading={isApproving}
              isApproved={isApproved}
              allPatients={allPatients}
              allProcedures={allProcedures}
              getAllPatients={getAllPatients}
              patients={allInsurancePatients}
              insuranceStatus={insuranceStatus}
              allTempPatients={allTempPatients}
              allPaymentTypes={allPaymentTypes}
              approvedPatients={approvedPatients}
              selectedInsurance={selectedInsurance}
              setMarkedProcedures={setMarkedProcedures}
              selectedPaymentType={selectedPaymentType}
              isApprovingInsurance={isApprovingInsurance}
              setSelectedPaymentType={setSelectedPaymentType}
              handleApproveInsurance={handleApproveInsurance}
              handleApprovePostPayment={handleApprovePostPayment}
              approvedPredeterminations={approvedPredeterminations}
              // setEmptyPatientDropdowns={setEmptyPatientDropdowns}
              predeterminationOrPostPayment={predeterminationOrPostPayment}
            />
          </div>
        </Card>

        {data && data.pdf_files && data.pdf_files.length > 0 && (
          <div style={{ marginTop: 30 }}>
            <p style={{ fontSize: 18 }}>Uploaded Files</p>

            <div style={{ display: "flex", alignItems: "center", flexDirection: "row", flexWrap: "wrap", gap: 20 }}>
              {data.pdf_files.map((file, index) => (
                <InsuranceUploadedFile key={index} file={file} />
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Approved Button */}
      {/* {predeterminationOrPostPayment === "predetermination" && (
        <div style={{ marginTop: 30, display: "flex", marginBottom: 90 }}>
          <Button
            type="primary"
            onClick={handleApproveInsurance}
            disabled={
              selectedInsurance.status !== "pending" ||
              isApproved ||
              (selectedInsurance.status === "pending" && emptyPatientDropdowns.length > 0) ||
              isApproving
            }
            style={{
              backgroundColor:
                selectedInsurance.status === "approved" || isApproved
                  ? "rgba(4, 201, 0, 0.1)"
                  : selectedInsurance.status !== "pending" || emptyPatientDropdowns.length > 0
                  ? "#F5F5F5"
                  : "#6B43B5",

              color:
                selectedInsurance.status === "approved" || isApproved
                  ? "#04C900"
                  : selectedInsurance.status !== "pending" || emptyPatientDropdowns.length > 0
                  ? "#848696"
                  : "white",

              border:
                selectedInsurance.status === "approved" || isApproved
                  ? "none"
                  : selectedInsurance.status === "pending" && emptyPatientDropdowns.length === 0 && "none",

              padding: "20px 50px",
              marginLeft: "auto",
            }}
            className={`${isApproved ? "approved-button" : ""}`}
          >
            {isApproving ? "Loading..." : insuranceStatus === "processing" || insuranceStatus === "pending" ? "Approve All" : "Approved"}
          </Button>
        </div>
      )} */}
    </div>
  );
}
